import { request } from "./http";
import { DEFAULT_REACTION_KIND } from "../ui/reactions/reactionKinds";
import { buildReactionTogglePayload, normalizeReactionKindForApi, REACTION_TARGET_TYPES } from "../ui/reactions/reactionModel";

function unwrapOk(payload) {
  if (payload && typeof payload === "object") {
    if ("item" in payload && payload.item !== undefined) return payload.item;
    if ("items" in payload && payload.items !== undefined) return payload.items;
    if ("data" in payload && payload.data !== undefined) return payload.data;
    if ("content" in payload && Array.isArray(payload.content)) return payload;
  }
  return payload;
}

function queryString(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    sp.set(key, String(value));
  });
  const raw = sp.toString();
  return raw ? `?${raw}` : "";
}

async function requestWithFallback(paths, options, retryStatuses = [404]) {
  const candidates = Array.isArray(paths) ? paths.filter(Boolean) : [];
  let lastError = null;
  for (const path of candidates) {
    try {
      return await request(path, options);
    } catch (err) {
      lastError = err;
      if (retryStatuses.includes(Number(err?.status))) continue;
      throw err;
    }
  }
  if (lastError) throw lastError;
  throw new Error("Endpoint is not configured");
}

export const podcastsApi = {
  getCatalog: async ({ q = "", topic = "", page = 0, size = 12 } = {}) => {
    const raw = await request(`/api/public/podcasts/catalog${queryString({
      q: String(q || "").trim(),
      topic: String(topic || "").trim(),
      page: Number(page) || 0,
      size: Number(size) || 12,
    })}`);
    return unwrapOk(raw) || {};
  },

  getPodcastProfile: async (podcastIdOrSlug, { tag = "", page = 0, size = 20 } = {}) => {
    if (!podcastIdOrSlug) return null;
    const raw = await request(`/api/public/podcasts/${encodeURIComponent(String(podcastIdOrSlug))}${queryString({
      tag: String(tag || "").trim(),
      page: Number(page) || 0,
      size: Number(size) || 20,
    })}`);
    return unwrapOk(raw) || null;
  },

  getEpisodeDetails: async (episodeId) => {
    if (!episodeId) return null;
    const raw = await requestWithFallback(
      [
        `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/public/podcast-episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/psychologist/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/admin/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || null;
  },

  getEpisodeComments: async (episodeId) => {
    if (!episodeId) return [];
    try {
      const raw = await requestWithFallback(
        [
          `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}/comments`,
          `/api/podcasts/episodes/${encodeURIComponent(String(episodeId))}/comments`,
          `/api/public/podcast-episodes/${encodeURIComponent(String(episodeId))}/comments`,
        ],
        { method: "GET" }
      );
      const payload = unwrapOk(raw);
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.items)) return payload.items;
      if (Array.isArray(payload?.content)) return payload.content;
      return [];
    } catch (err) {
      if (Number(err?.status) === 404) return [];
      throw err;
    }
  },

  postEpisodeComment: async (episodeId, payload = {}) => {
    if (!episodeId) return null;
    const raw = await requestWithFallback(
      [
        `/api/podcasts/episodes/${encodeURIComponent(String(episodeId))}/comments`,
        `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}/comments`,
        `/api/public/podcast-episodes/${encodeURIComponent(String(episodeId))}/comments`,
      ],
      {
        method: "POST",
        json: payload,
      }
    );
    return unwrapOk(raw) || raw || null;
  },

  reportPodcast: async (podcastId, payload = {}) => {
    if (!podcastId) return null;
    const raw = await requestWithFallback(
      [
        `/api/podcasts/${encodeURIComponent(String(podcastId))}/report`,
        `/api/public/podcasts/${encodeURIComponent(String(podcastId))}/report`,
      ],
      { method: "POST", json: payload },
      [404, 405, 501]
    );
    return unwrapOk(raw) || raw || null;
  },

  reportEpisode: async (episodeId, payload = {}) => {
    if (!episodeId) return null;
    const raw = await requestWithFallback(
      [
        `/api/podcasts/episodes/${encodeURIComponent(String(episodeId))}/report`,
        `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}/report`,
      ],
      { method: "POST", json: payload },
      [404, 405, 501]
    );
    return unwrapOk(raw) || raw || null;
  },

  reportEpisodeComment: async (commentId, payload = {}) => {
    if (!commentId) return null;
    const raw = await requestWithFallback(
      [
        `/api/podcasts/comments/${encodeURIComponent(String(commentId))}/report`,
        `/api/public/podcasts/comments/${encodeURIComponent(String(commentId))}/report`,
      ],
      { method: "POST", json: payload },
      [404, 405, 501]
    );
    return unwrapOk(raw) || raw || null;
  },

  toggleEpisodeReaction: async (episodeId, reactionType = DEFAULT_REACTION_KIND) => {
    if (!episodeId) return null;
    const type = normalizeReactionKindForApi(reactionType, DEFAULT_REACTION_KIND);
    const body = buildReactionTogglePayload({
      reaction: type,
      targetType: REACTION_TARGET_TYPES.PODCAST_EPISODE,
      targetId: episodeId,
    });
    const raw = await requestWithFallback(
      [
        `/api/podcasts/episodes/${encodeURIComponent(String(episodeId))}/reactions/toggle`,
        `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}/reactions/toggle`,
        `/api/public/podcast-episodes/${encodeURIComponent(String(episodeId))}/reactions/toggle`,
      ],
      {
        method: "POST",
        json: body,
      }
    );
    return unwrapOk(raw) || raw || null;
  },

  toggleEpisodeCommentReaction: async (commentId, reactionType = DEFAULT_REACTION_KIND) => {
    if (!commentId) return null;
    const type = normalizeReactionKindForApi(reactionType, DEFAULT_REACTION_KIND);
    const body = buildReactionTogglePayload({
      reaction: type,
      targetType: REACTION_TARGET_TYPES.PODCAST_COMMENT,
      targetId: commentId,
    });
    const raw = await requestWithFallback(
      [
        `/api/podcasts/comments/${encodeURIComponent(String(commentId))}/reactions/toggle`,
        `/api/public/podcasts/comments/${encodeURIComponent(String(commentId))}/reactions/toggle`,
      ],
      {
        method: "POST",
        json: body,
      }
    );
    return unwrapOk(raw) || raw || null;
  },

  registerListen: async (episodeId) => {
    if (!episodeId) return null;
    const raw = await requestWithFallback(
      [
        `/api/public/podcasts/episodes/${encodeURIComponent(String(episodeId))}/listen`,
        `/api/psychologist/podcasts/episodes/${encodeURIComponent(String(episodeId))}/listen`,
        `/api/admin/podcasts/episodes/${encodeURIComponent(String(episodeId))}/listen`,
      ],
      { method: "POST" },
      [404, 403]
    );
    return unwrapOk(raw) || null;
  },

  subscribe: async (podcastId) => {
    const raw = await request(`/api/me/podcasts/${encodeURIComponent(String(podcastId))}/subscribe`, {
      method: "POST",
    });
    return unwrapOk(raw) || raw || null;
  },

  unsubscribe: async (podcastId) => {
    const raw = await request(`/api/me/podcasts/${encodeURIComponent(String(podcastId))}/subscribe`, {
      method: "DELETE",
    });
    return unwrapOk(raw) || raw || null;
  },

  saveEpisode: async (episodeId) => {
    const raw = await request(`/api/me/podcasts/episodes/${encodeURIComponent(String(episodeId))}/save`, {
      method: "POST",
    });
    return unwrapOk(raw) || raw || null;
  },

  unsaveEpisode: async (episodeId) => {
    const raw = await request(`/api/me/podcasts/episodes/${encodeURIComponent(String(episodeId))}/save`, {
      method: "DELETE",
    });
    return unwrapOk(raw) || raw || null;
  },

  getMyPodcasts: async ({ page = 0, size = 20 } = {}) => {
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts${queryString({ page: Number(page) || 0, size: Number(size) || 20 })}`,
        `/api/admin/podcasts${queryString({ page: Number(page) || 0, size: Number(size) || 20 })}`,
        `/api/me/podcasts${queryString({ page: Number(page) || 0, size: Number(size) || 20 })}`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || raw || { content: [] };
  },

  getMyPodcastStats: async () => {
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/stats`,
        `/api/admin/podcasts/stats`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || raw || {};
  },

  getMyPodcast: async (podcastId) => {
    if (!podcastId) return null;
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/${encodeURIComponent(String(podcastId))}`,
        `/api/admin/podcasts/${encodeURIComponent(String(podcastId))}`,
        `/api/public/podcasts/${encodeURIComponent(String(podcastId))}`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  getMyPodcastEpisodes: async (podcastId, { page = 0, size = 20 } = {}) => {
    if (!podcastId) return { content: [] };
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/${encodeURIComponent(String(podcastId))}/episodes${queryString({
          page: Number(page) || 0,
          size: Number(size) || 20,
        })}`,
        `/api/admin/podcasts/${encodeURIComponent(String(podcastId))}/episodes${queryString({
          page: Number(page) || 0,
          size: Number(size) || 20,
        })}`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || raw || { content: [] };
  },

  getMyPodcastEpisode: async (episodeId) => {
    if (!episodeId) return null;
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/admin/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
      ],
      { method: "GET" },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },



  createMyPodcast: async (payload = {}) => {
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts`,
        `/api/admin/podcasts`,
      ],
      { method: "POST", json: payload },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  updateMyPodcast: async (podcastId, payload = {}) => {
    if (!podcastId) throw new Error("podcastId is required");
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/${encodeURIComponent(String(podcastId))}`,
        `/api/admin/podcasts/${encodeURIComponent(String(podcastId))}`,
      ],
      { method: "PUT", json: payload },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  createMyPodcastEpisode: async (podcastId, payload = {}, requestOptions = {}) => {
    if (!podcastId) throw new Error("podcastId is required");
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/${encodeURIComponent(String(podcastId))}/episodes`,
        `/api/admin/podcasts/${encodeURIComponent(String(podcastId))}/episodes`,
      ],
      { method: "POST", json: payload, ...requestOptions },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  updateMyPodcastEpisode: async (episodeId, payload = {}, requestOptions = {}) => {
    if (!episodeId) throw new Error("episodeId is required");
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/admin/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
        `/api/podcasts/episodes/${encodeURIComponent(String(episodeId))}`,
      ],
      { method: "PUT", json: payload, ...requestOptions },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  syncMyPodcastEpisodeAegis: async (episodeId, requestOptions = {}) => {
    if (!episodeId) throw new Error("episodeId is required");
    const raw = await requestWithFallback(
      [
        `/api/psychologist/podcasts/episodes/${encodeURIComponent(String(episodeId))}/aegis/sync`,
        `/api/admin/podcasts/episodes/${encodeURIComponent(String(episodeId))}/aegis/sync`,
      ],
      { method: "POST", ...requestOptions },
      [404, 403]
    );
    return unwrapOk(raw) || raw || null;
  },

  getMySubscriptions: async ({ page = 0, size = 10 } = {}) => {
    const raw = await request(`/api/me/podcasts/subscriptions${queryString({
      page: Number(page) || 0,
      size: Number(size) || 10,
    })}`);
    return unwrapOk(raw) || raw || [];
  },

  getMySavedEpisodes: async ({ page = 0, size = 10 } = {}) => {
    const raw = await request(`/api/me/podcasts/saved-episodes${queryString({
      page: Number(page) || 0,
      size: Number(size) || 10,
    })}`);
    return unwrapOk(raw) || raw || [];
  },
};
